import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'
import { MapPinIcon } from '@heroicons/react/24/outline'

import { Input } from '../../../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import type { OnboardingFormValues } from '../types'
import { OnboardingStepCard } from '../components/OnboardingStepCard'
import { onboardingFieldLabelSx } from './stepStyles'

interface OrgAddressStepProps {
  control: Control<OnboardingFormValues>
  errors: FieldErrors<OnboardingFormValues>
}

export const OrgAddressStep: React.FC<OrgAddressStepProps> = ({ control, errors }) => {
  const { t } = useTranslation()

  return (
    <OnboardingStepCard
      icon={<MapPinIcon style={{ width: 20, height: 20, color: INTERAX_COLORS.sun }} />}
      title={t('org.onboarding.stepAddressTitle')}
      hint={t('org.onboarding.stepAddressHint')}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography sx={onboardingFieldLabelSx}>{t('org.onboarding.streetLabel')}</Typography>
          <Controller
            name="street"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                placeholder={t('org.onboarding.streetPlaceholder')}
                fullWidth
                required
                error={Boolean(errors.street)}
                errorMessage={errors.street?.message}
                data-testid="org-onboarding-street"
              />
            )}
          />
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row', md: 'row' }} spacing={{ xs: 2.5, sm: 2, md: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={onboardingFieldLabelSx}>{t('org.onboarding.postalCodeLabel')}</Typography>
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('org.onboarding.postalCodePlaceholder')}
                  fullWidth
                  error={Boolean(errors.postalCode)}
                  errorMessage={errors.postalCode?.message}
                  data-testid="org-onboarding-postalcode"
                />
              )}
            />
          </Box>
          <Box sx={{ flex: 2, minWidth: 0 }}>
            <Typography sx={onboardingFieldLabelSx}>{t('org.onboarding.cityLabel')}</Typography>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <Input
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('org.onboarding.cityPlaceholder')}
                  fullWidth
                  required
                  error={Boolean(errors.city)}
                  errorMessage={errors.city?.message}
                  data-testid="org-onboarding-city"
                />
              )}
            />
          </Box>
        </Stack>

        <Box>
          <Typography sx={onboardingFieldLabelSx}>{t('org.onboarding.countryLabel')}</Typography>
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                placeholder={t('org.onboarding.countryPlaceholder')}
                fullWidth
                required
                error={Boolean(errors.country)}
                errorMessage={errors.country?.message}
                data-testid="org-onboarding-country"
              />
            )}
          />
        </Box>
      </Stack>
    </OnboardingStepCard>
  )
}
